import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Spinner, Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier, getSupplierAccountingOptions } from '../../../services/SuppliersService';
import SupplierAccountingFields, { accountingFromSupplier, accountingPayload, emptyAccounting } from './SupplierAccountingFields';

// Suppliers directory: contact details and the accounting details used for Rompslomp. Material price tiers are NOT shown or edited here
// any more (the data is kept and still feeds Supplier Analysis); a save never sends `prices`, so stored tiers are never touched.
export default function Suppliers() {
    const { t } = useTranslation();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', ...emptyAccounting() });
    const [accOptions, setAccOptions] = useState({ cost_accounts: [], treatments: [] });

    const loadSuppliers = async () => {
        try {
            setLoading(true);
            const res = await getSuppliers();
            setSuppliers(res.data || []);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('suppliers.errors.load');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSuppliers();
        (async () => {
            try { setAccOptions((await getSupplierAccountingOptions()).data); } catch (_) { /* categories are optional here */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredSuppliers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return suppliers;
        return suppliers.filter(
            (s) =>
                (s.name || '').toLowerCase().includes(q) ||
                (s.legal_name || '').toLowerCase().includes(q) ||
                (s.phone || '').toLowerCase().includes(q) ||
                (s.email || '').toLowerCase().includes(q) ||
                (s.address || '').toLowerCase().includes(q) ||
                (s.aliases || []).some((a) => a.toLowerCase().includes(q))
        );
    }, [suppliers, searchQuery]);

    const openAdd = () => {
        setEditingId(null);
        setForm({ name: '', phone: '', email: '', address: '', ...emptyAccounting() });
        setShowModal(true);
    };

    const openEdit = (supplier) => {
        setEditingId(supplier.id);
        setForm({
            name: supplier.name || '',
            phone: supplier.phone || '',
            email: supplier.email || '',
            address: supplier.address || '',
            ...accountingFromSupplier(supplier),
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
    };

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = (form.name || '').trim();
        if (!name) {
            toast.error(t('suppliers.errors.nameRequired'));
            return;
        }

        const payload = {
            name,
            phone: (form.phone || '').trim() || null,
            email: (form.email || '').trim() || null,
            address: (form.address || '').trim() || null,
            ...accountingPayload(form),
        };

        try {
            if (editingId) {
                await updateSupplier(editingId, payload);
                toast.success(t('suppliers.updated'));
            } else {
                await createSupplier(payload);
                toast.success(t('suppliers.added'));
            }
            closeModal();
            loadSuppliers();
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('suppliers.errors.save');
            toast.error(msg);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(t('suppliers.deleteConfirm', { name }))) return;
        try {
            await deleteSupplier(id);
            toast.success(t('suppliers.deleted'));
            loadSuppliers();
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('suppliers.errors.delete');
            toast.error(msg);
        }
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" className="text-primary" />
                    <p className="mt-2 text-muted mb-0">{t('suppliers.loadingSuppliers')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h4 className="heading mb-0">{t('suppliers.title')}</h4>
                                <span className="text-muted">{t('suppliers.subtitle')}</span>
                            </div>
                            <Button variant="primary" size="sm" onClick={openAdd}>
                                <i className="fa-solid fa-plus me-1" /> {t('suppliers.addButton')}
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('suppliers.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </Col>
                            </Row>

                            {filteredSuppliers.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">{t('suppliers.noSuppliers')}</p>
                                    <Button variant="outline-primary" onClick={openAdd}>
                                        <i className="fa-solid fa-plus me-1" /> {t('suppliers.addFirst')}
                                    </Button>
                                </div>
                            ) : (
                                <Table responsive bordered className="suppliers-main-table">
                                    <thead>
                                        <tr>
                                            <th>{t('suppliers.columns.name')}</th>
                                            <th>{t('suppliers.columns.phone')}</th>
                                            <th>{t('suppliers.columns.email')}</th>
                                            <th>{t('suppliers.columns.address')}</th>
                                            <th>{t('suppliers.columns.rompslomp')}</th>
                                            <th>{t('suppliers.columns.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSuppliers.map((s) => (
                                            <tr key={s.id}>
                                                <td><strong>{s.name}</strong></td>
                                                <td>{s.phone || '-'}</td>
                                                <td>{s.email || '-'}</td>
                                                <td className="supplier-address-cell" title={s.address || ''}>{s.address || '-'}</td>
                                                <td data-testid={`supplier-rompslomp-${s.id}`}>
                                                    {s.rompslomp_contact_id ? t('suppliers.accounting.inRompslomp') : t('suppliers.accounting.notInRompslomp')}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Button variant="primary" size="sm" onClick={() => openEdit(s)}>
                                                            <i className="fa-solid fa-pen me-1" /> {t('common.edit')}
                                                        </Button>
                                                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(s.id, s.name)}>
                                                            <i className="fa-solid fa-trash me-1" /> {t('common.delete')}
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}

                            {filteredSuppliers.length > 0 && (
                                <div className="mt-3 text-muted small">
                                    {t('suppliers.showingCount', { shown: filteredSuppliers.length, total: suppliers.length })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Modal show={showModal} onHide={closeModal} size="xl" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{editingId ? t('suppliers.modal.editTitle') : t('suppliers.modal.addTitle')}</Modal.Title>
                        </Modal.Header>
                        <Form onSubmit={handleSubmit}>
                            <Modal.Body>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="mb-2" controlId="supplier-name">
                                            <Form.Label>{t('suppliers.modal.nameLabel')}</Form.Label>
                                            <Form.Control
                                                value={form.name}
                                                onChange={(e) => updateField('name', e.target.value)}
                                                placeholder={t('suppliers.modal.namePlaceholder')}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2" controlId="supplier-phone">
                                            <Form.Label>{t('suppliers.modal.phoneLabel')}</Form.Label>
                                            <Form.Control
                                                value={form.phone}
                                                onChange={(e) => updateField('phone', e.target.value)}
                                                placeholder={t('suppliers.modal.phonePlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-2" controlId="supplier-email">
                                            <Form.Label>{t('suppliers.modal.emailLabel')}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => updateField('email', e.target.value)}
                                                placeholder={t('suppliers.modal.emailPlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2" controlId="supplier-address">
                                            <Form.Label>{t('suppliers.modal.addressLabel')}</Form.Label>
                                            <Form.Control
                                                value={form.address}
                                                onChange={(e) => updateField('address', e.target.value)}
                                                placeholder={t('suppliers.modal.addressPlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" />
                                <SupplierAccountingFields values={form} onChange={updateField} options={accOptions} idPrefix="supplier-edit" />
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={closeModal}>
                                    {t('common.cancel')}
                                </Button>
                                <Button variant="primary" type="submit">
                                    {editingId ? t('suppliers.modal.updateButton') : t('suppliers.addButton')}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    </Modal>
                </div>
            </div>
        </div>
    );
}
